/**
 * Validação do arquivo de audiência, antes de qualquer envio (FR-018, FR-052o).
 *
 * Recusar cedo é o ponto: a criação da campanha é síncrona e bloqueante, então um
 * arquivo aceito aqui e recusado lá adiante custa ao operador o briefing inteiro
 * preenchido.
 *
 * Nomeado de forma genérica (2026-09-22): hoje só CSV é aceito, e é isso que
 * `hasSupportedFileExtension`/`splitCsvColumns` fazem — a especificidade fica só
 * onde a lógica de fato é de CSV. O que é sobre o arquivo em si (teto de
 * tamanho, tipo de recusa, entrada de validação) não carrega o formato no nome,
 * para não precisar renomear tudo de novo se um segundo formato for aceito. A
 * interface da campanha continua dizendo "CSV" à vontade — é a realidade de
 * hoje, e é isso que este módulo valida.
 */

// Teto próprio do fluxo offsite, separado do limite de 2 GB do envio de audiência de
// publisher. 95 MB, não 100: a Publya recusa acima de 100 MB (0131), e a margem
// evita a ambiguidade decimal/binário do "100 MB" documentado.
export const AUDIENCE_FILE_MAX_BYTES = 95 * 1024 * 1024

// Um identificador por linha (FR-018): hash de e-mail, identificador de CRM ou de
// cookie. O cabeçalho precisa trazer ao menos um deles.
export const AUDIENCE_IDENTIFIER_COLUMNS = ['email', 'crm_id', 'cookie_id']

export type AudienceFileRejection =
  | 'extension'
  | 'empty'
  | 'tooLarge'
  | 'header'
  | 'noRows'

const normalizeColumn = (column: string) =>
  column.trim().replaceAll(/^"|"$/g, '').toLowerCase().replaceAll(/\s+/g, '_')

// Único ponto que sabe qual é o formato aceito hoje — CSV. Um segundo formato
// vira uma segunda checagem aqui dentro, sem mexer em quem chama.
export const hasSupportedFileExtension = (filename: string) =>
  /\.csv$/i.test((filename || '').trim())

export const splitCsvColumns = (line: string) =>
  line.split(/[,;]/).map(normalizeColumn)

/**
 * Lê só o cabeçalho e a primeira linha de dados. Recebe um trecho do início do
 * arquivo, nunca o arquivo inteiro — 95 MB de texto em memória no navegador é
 * exatamente o que este fluxo evita.
 */
export const validateAudienceFileSample = (
  sample: string
): AudienceFileRejection | null => {
  const lines = (sample || '')
    .split(/\r?\n/)
    .map(line => line.trim())
    .filter(Boolean)

  if (!lines.length) return 'empty'

  const columns = splitCsvColumns(lines[0])
  const hasIdentifier = columns.some(column =>
    AUDIENCE_IDENTIFIER_COLUMNS.includes(column)
  )

  if (!hasIdentifier) return 'header'
  if (lines.length < 2) return 'noRows'

  return null
}

export const validateAudienceFile = ({
  name,
  size
}: {
  name: string
  size: number
}): AudienceFileRejection | null => {
  if (!hasSupportedFileExtension(name)) return 'extension'
  if (!size) return 'empty'
  if (size > AUDIENCE_FILE_MAX_BYTES) return 'tooLarge'

  return null
}

// Exibição do arquivo já enviado (KB abaixo de 1 MB, MB a partir daí) — não existe
// um formatador de tamanho compartilhado no admin hoje.
export const formatAudienceFileSize = (bytes: number): string => {
  if (!bytes || bytes < 0) return '0 KB'

  const megabytes = bytes / (1024 * 1024)
  if (megabytes >= 1) return `${megabytes.toFixed(1)} MB`

  const kilobytes = Math.max(1, Math.round(bytes / 1024))
  return `${kilobytes} KB`
}

// 64 KB cobre com folga o cabeçalho e a primeira linha de qualquer arquivo real.
const SAMPLE_BYTES = 64 * 1024

export const readAudienceFileSample = (file: Blob): Promise<string> =>
  file.slice(0, SAMPLE_BYTES).text()
